import React, { FC } from 'react';
import { Series } from 'types/Asset';
import { getSeriesDetailUrl } from 'utils/fnUrl';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import { useBookmarks } from 'providers/useBookmarks/BookmarksContext';
import { StandardCover } from 'types/Cover';
import { CardContext, ImageAspectRatio, ModuleType } from 'types/Common';
import { CardTrackingData } from 'types/Tracking';
import { getSeriesCardContentMarkerValue } from 'utils/fnContentMarker';
import { BookmarkSectionType } from 'types/Bookmarks';
import { useApp } from 'providers/useApp/AppContext';
import { useChannels } from 'providers/useChannels/useChannels';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';

export const SeriesCardPresenter: FC<{
    asset: Series;
    type?: ModuleType;
    aspectRatio?: ImageAspectRatio;
    cardContext: CardContext;
    trackingData: CardTrackingData;
}> = ({ asset, type, cardContext, trackingData }) => {
    const { id, title, image, customImage, genres, firstAvailability, lastEpisodeFirstAvailability, isVOD, imageAspectRatio, channelId } =
        asset;

    const metaData = genres?.length ? genres.slice(0, 2).join(', ') : '';
    const locationParamsPath = useDetailLocationParams();
    const { isBookmarked } = useBookmarks();
    const { appLanguage } = useApp();
    const { getChannelById } = useChannels();
    const channelLogo = getChannelById(channelId)?.logo;

    const { handleContentFocus, handleContentFocusOut, handleContentClick } = useYouboraContentTracking();

    const getYouboraParams = (): ContentTrackingParams => ({
        section: trackingData['data-module-title'] || 'Series Section',
        sectionOrder: trackingData['data-stripe-position'] || 1,
        column: trackingData['data-card-position'] || 1,
        row: 1,
        contentId: 'null',
        contentLink: id,
        contentLinkType: 'seriesId',
    });

    const handleCardFocus = () => {
        handleContentFocus(getYouboraParams());
    };

    const handleCardFocusOut = () => {
        handleContentFocusOut();
    };

    const handleCardClick = () => {
        handleContentClick(getYouboraParams());
    };

    const contentTrackingProps = {
        onMouseEnter: handleCardFocus,
        onMouseLeave: handleCardFocusOut,
        onClick: handleCardClick,
    };

    const cover: StandardCover = {
        uuid: id + title,
        id,
        title,
        image,
        landscapeImage: image,
        customImage,
        path: getSeriesDetailUrl(asset, locationParamsPath, appLanguage),
        metaData,
        circle: false,
        landscape: true,
        assetType: asset.type,
        rawData: asset,
        channelImage: channelLogo,
        contentMarker: getSeriesCardContentMarkerValue(firstAvailability, isVOD, lastEpisodeFirstAvailability),
        hideDescription: true,
        renderPlayIcon: false,
        isFavorite: isBookmarked(id, BookmarkSectionType.BROADCASTS_AND_SERIES),
    };

    const isRounded = type === ModuleType.ROUNDED || imageAspectRatio === '1:1';

    return (
        <Card
            variant={isRounded ? CardVariant.CIRCLE : CardVariant.LANDSCAPE}
            context={cardContext}
            asset={cover}
            {...trackingData}
            {...contentTrackingProps}
        />
    );
};
